const DB_NAME = 'HectorDB';
const DB_VERSION = 1;
const STORE_NAME = 'completedJobs';

class IndexedDBManager {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('jobId', 'jobId', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  async addCompletedJob(jobData) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const jobRecord = {
        ...jobData,
        timestamp: Date.now(),
        id: Date.now()
      };
      
      const request = store.add(jobRecord);
      
      request.onsuccess = () => {
        console.log('Completed job added to IndexedDB:', jobRecord);
        resolve(request.result);
      };
      
      request.onerror = () => {
        console.error('Error adding completed job:', request.error);
        reject(request.error);
      };
    });
  }

  async getCompletedJobsByDateRange(startDate, endDate) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      const range = IDBKeyRange.bound(startDate, endDate);
      const request = index.getAll(range);
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = () => {
        console.error('Error fetching completed jobs by date range:', request.error);
        reject(request.error);
      };
    });
  }

  async clearOldJobs(olderThanDays = 7) {
    await this.init();
    
    const cutoffTime = Date.now() - (olderThanDays * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('timestamp');
      const range = IDBKeyRange.upperBound(cutoffTime);
      const request = index.openCursor(range);
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          console.log('Old completed jobs cleared');
          resolve();
        }
      };
      
      request.onerror = () => {
        console.error('Error clearing old jobs:', request.error);
        reject(request.error);
      };
    });
  }

  async deleteJob(jobId) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('jobId');
      const request = index.getKey(jobId);
      
      request.onsuccess = () => {
        const key = request.result;
        if (key) {
          const deleteRequest = store.delete(key);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject(deleteRequest.error);
        } else {
          resolve();
        }
      };
      
      request.onerror = () => {
        console.error('Error finding job to delete:', request.error);
        reject(request.error);
      };
    });
  }
}

const indexedDBManager = new IndexedDBManager();

export default indexedDBManager;
